import {
  Avatar,
  Box,
  type BoxProps,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  type MenuProps,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  LuChevronDown,
  LuChevronRight,
  LuChevronUp,
  LuLogOut,
  LuUser,
} from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { Logo } from '@/components/Logo';
import { useGetAccountQuery } from '@/features/auth';
import { COACH_NAV_BAR_HEIGHT } from '@/features/coach';
import { NavBarLanguageMenu } from '@/features/common';

import {
  ACCOUNT_CV_PATH,
  ACCOUNT_INTERVIEW_PATH,
  ACCOUNT_PATH,
} from './constants';

const AccountNavBarAccountMenu = ({ ...rest }: Omit<MenuProps, 'children'>) => {
  const navigate = useNavigate();
  const account = useGetAccountQuery();
  const { t } = useTranslation(['auth']);

  const ACCOUNT_MENU_OPTIONS = [
    {
      title: t('auth:admin.layout.accountMenu.myProfile'),
      icon: LuUser,
      url: `${ACCOUNT_PATH}/personal-info`,
      colorScheme: 'brand',
    },
    {
      title: t('auth:admin.layout.accountMenu.logout'),
      icon: LuLogOut,
      url: `/logout?redirect=${ACCOUNT_PATH}/login`,
      colorScheme: 'gray',
    },
  ] as const;

  return (
    <Box color="codGray.900">
      <Menu placement="bottom-end" {...rest}>
        {({ isOpen }) => (
          <>
            <MenuButton
              isActive={isOpen}
              as={Button}
              variant="link"
              h={12}
              rightIcon={
                isOpen ? (
                  <Icon as={LuChevronUp} fontSize="20px" />
                ) : (
                  <Icon as={LuChevronDown} fontSize="20px" />
                )
              }
              _hover={{
                backgroundColor: 'brand.25',
              }}
            >
              <Avatar
                showBorder
                size="md"
                name={`${account.data?.name} ${account.data?.surname}`}
                src={account.data?.profileImage}
              />
            </MenuButton>

            <MenuList
              minWidth="17rem"
              overflow="hidden"
              p={3}
              borderColor="gray.50"
              shadow="md"
              borderRadius={12}
            >
              <MenuGroup
                title={account.data?.email ?? ''}
                noOfLines={1}
                m={0}
                color="gray.900"
                fontWeight="normal"
              />
              <MenuDivider />

              <VStack gap={3}>
                {ACCOUNT_MENU_OPTIONS.map((item) => (
                  <MenuItem
                    key={item.url}
                    p={1}
                    border="1px solid"
                    borderColor="gray.50"
                    transition="linear 0.1s"
                    borderRadius={8}
                    fontWeight="medium"
                    _hover={{
                      borderColor: `${item.colorScheme}.200`,
                      bg: `${item.colorScheme}.25`,
                    }}
                    icon={
                      <Center
                        w="2.25rem"
                        h="2.25rem"
                        bg={`${item.colorScheme}.50`}
                        borderRadius={8}
                      >
                        <Icon
                          as={item.icon}
                          fontSize="xl"
                          color={`${item.colorScheme}.500`}
                        />
                      </Center>
                    }
                    onClick={() => navigate(item.url)}
                  >
                    <HStack justifyContent="space-between">
                      <Text>{item.title}</Text>
                      {item.colorScheme !== 'gray' && (
                        <Icon
                          as={LuChevronRight}
                          fontSize="lg"
                          color="gray.500"
                        />
                      )}
                    </HStack>
                  </MenuItem>
                ))}
              </VStack>
            </MenuList>
          </>
        )}
      </Menu>
    </Box>
  );
};

export const AccountNavBar = (props: BoxProps) => {
  const { t } = useTranslation(['candidate']);
  const navigate = useNavigate();

  return (
    <Box {...props}>
      <Flex
        zIndex="sticky"
        position="fixed"
        top="0"
        insetStart="0"
        insetEnd="0"
        color="gray.50"
        align="center"
        justify="center"
        pt="safe-top"
        px="4"
        h={COACH_NAV_BAR_HEIGHT}
        bg="white"
        boxShadow="layout"
        borderBottom="1px solid transparent"
      >
        <Flex
          grow={1}
          align="center"
          justify="space-between"
          maxW="container.xl"
        >
          <HStack gap={6}>
            <Box mx={0}>
              <Logo />
            </Box>
            <HStack>
              <Button
                variant="link"
                onClick={() => navigate(ACCOUNT_INTERVIEW_PATH)}
              >
                {t('candidate:settings.myInterviews')}
              </Button>
              <Button variant="link" onClick={() => navigate(ACCOUNT_CV_PATH)}>
                {t('candidate:settings.myCVAnalyses')}
              </Button>
            </HStack>
          </HStack>
          <HStack>
            <AccountNavBarAccountMenu />
            <NavBarLanguageMenu />
          </HStack>
        </Flex>
      </Flex>
      <Box h={COACH_NAV_BAR_HEIGHT} />
    </Box>
  );
};
